// A setting with layered defaults (`@sirius/contracts`' `inheritableFields`):
// a record holds its own value, null for "Use default", and the API sends
// what null resolves to as `inherited<Key>`. Forms pick it in an
// `InheritableSelect` (`~/components/inheritableSelect`).

import type { TFunction } from 'i18next'

/** Select value for "Use default" (own value null); selects can't hold null. */
export const USE_DEFAULT = '__default__'

/** The select's value for a record's own value. */
export function fromOwn(own: string | null | undefined): string {
  return own ?? USE_DEFAULT
}

/** The select's value as the API's own value: null for Use default. */
export function toOwn<Value extends string>(
  choice: string | undefined,
  isValue: (value: unknown) => value is Value,
): Value | null {
  return isValue(choice) ? choice : null
}

/** The select's first option: "Use default (Русский)", plain while unknown. */
export function useDefaultLabel(t: TFunction, hint?: string | null): string {
  return hint === undefined || hint === null
    ? t('shell.common.useDefault')
    : t('shell.common.useDefaultWith', { name: hint })
}

/**
 * A show page's value: the own value's name, else "Default (Русский)" with
 * what it inherits (plain "Default" while unknown).
 */
export function ownOrDefaultLabel<Value>(
  own: Value | null | undefined,
  inherited: Value | null | undefined,
  name: (value: Value) => string,
  t: TFunction,
): string {
  if (own !== null && own !== undefined) {
    return name(own)
  }
  return inherited === null || inherited === undefined
    ? t('shell.common.default')
    : t('shell.common.defaultWith', { name: name(inherited) })
}

/** What the record runs on: its own value, else the inherited one. */
export function effective<Value>(
  own: Value | null | undefined,
  inherited: Value,
): Value {
  return own ?? inherited
}

/**
 * `{ [key]: choice }` when the choice differs from the record's own value,
 * else nothing: a shared record's save sends only what the user changed, so
 * it never puts back what someone else set since the page loaded.
 */
export function changedOnly<Key extends string, Value>(
  key: Key,
  own: Value | null | undefined,
  choice: Value | null,
): { [Name in Key]?: Value | null } {
  return (own ?? null) === choice
    ? {}
    : ({ [key]: choice } as { [Name in Key]?: Value | null })
}
